import { AlertTriangle, HelpCircle } from 'lucide-react';
import { useEffect, useRef, useSyncExternalStore } from 'react';
import { Button } from './button';

/**
 * Onay penceresi: `window.confirm` yerine.
 *
 * Tarayıcının kendi kutusu üstünde "amzsa.kerimsametkilic.com web sitesinin mesajı" yazıyor, koyu
 * temaya uymuyor ve açıkken sekmenin bütün işini durduruyordu (canlı ilerleme, sayaçlar).
 *
 * Kullanım: `if (!(await onayla('Bu kayıt silinsin mi?'))) return;`
 * Pencere `App`'te bir kez çiziliyor (`<OnayPenceresi />`).
 */
export interface OnaySecenekleri {
  metin: string;
  baslik?: string;
  onayEtiketi?: string;
  /** Verilmezse metinden anlaşılıyor (sil, kaldır, sıfırla, geri alınamaz, sürdürülemez…). */
  tehlikeli?: boolean;
}

/** Açılıştan sonra onayın çalışmadığı süre: çift tıklamanın ikincisi soruyu okunmadan onaylamasın. */
export const ONAY_KILIDI_MS = 300;

interface Bekleyen extends OnaySecenekleri {
  acilis: number;
  bitir: (evet: boolean) => void;
}

let bekleyen: Bekleyen | null = null;
const dinleyiciler = new Set<() => void>();

function degisti(yeni: Bekleyen | null) {
  bekleyen = yeni;
  for (const d of dinleyiciler) d();
}

export function onayla(girdi: string | OnaySecenekleri): Promise<boolean> {
  const secenekler = typeof girdi === 'string' ? { metin: girdi } : girdi;
  // Aynı anda ikinci soru gelirse eskisi "hayır" sayılıyor: iki işlem üst üste onaylanamasın
  bekleyen?.bitir(false);
  return new Promise((coz) => {
    degisti({
      ...secenekler,
      acilis: Date.now(),
      bitir: (evet) => {
        degisti(null);
        coz(evet);
      },
    });
  });
}

/** Küçük harfe `tr-TR` ile çevrilmiş metne uygulanıyor: `/i` "GERİ ALINAMAZ"ı yakalamıyordu (İ/I). */
const TEHLIKELI = /\b(sil|kaldır|sıfırla)|geri alınamaz|geri getirilemez|sürdürülemez/;

export function OnayPenceresi() {
  const soru = useSyncExternalStore(
    (d) => {
      dinleyiciler.add(d);
      return () => dinleyiciler.delete(d);
    },
    () => bekleyen,
  );
  const vazgec = useRef<HTMLButtonElement>(null);
  const onay = useRef<HTMLButtonElement>(null);
  const tehlikeli = soru
    ? (soru.tehlikeli ?? TEHLIKELI.test(soru.metin.toLocaleLowerCase('tr-TR')))
    : false;

  useEffect(() => {
    if (!soru) return;
    const onceki = document.activeElement as HTMLElement | null;
    // Tehlikeli işlemde Enter'la yanlışlıkla onaylanmasın: odak "Vazgeç"te
    (tehlikeli ? vazgec : onay).current?.focus();
    // Geri tuşu ekranı değiştiriyor: soru açık kalıp başka ekranın üstünde onaylanmasın
    const geriTusu = () => soru.bitir(false);
    window.addEventListener('popstate', geriTusu);
    // Belgeden dinleniyor: metne ya da arka plana tıklanınca odak düğmeden çıkıyor, tuşlar yine çalışsın
    const tus = (e: KeyboardEvent) => {
      e.stopPropagation();
      const dugmede =
        document.activeElement === onay.current || document.activeElement === vazgec.current;
      if (e.key === 'Escape') {
        soru.bitir(false);
      } else if (e.key === 'Enter') {
        // Tarayıcının "Enter = tıklama"sı yerine: hızlı ya da basılı kalan Enter sayılmıyor
        e.preventDefault();
        if (!dugmede) (tehlikeli ? vazgec : onay).current?.focus();
        else if (!e.repeat && Date.now() - soru.acilis >= ONAY_KILIDI_MS) {
          soru.bitir(document.activeElement === onay.current);
        }
      } else if (e.key === 'Tab') {
        // Odak pencerenin dışına çıkmasın
        e.preventDefault();
        (document.activeElement === onay.current ? vazgec : onay).current?.focus();
      }
    };
    document.addEventListener('keydown', tus, true);
    return () => {
      window.removeEventListener('popstate', geriTusu);
      document.removeEventListener('keydown', tus, true);
      onceki?.focus?.();
    };
  }, [soru, tehlikeli]);

  if (!soru) return null;

  const kilitli = () => Date.now() - soru.acilis < ONAY_KILIDI_MS;
  const Simge = tehlikeli ? AlertTriangle : HelpCircle;
  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-black/40 p-4">
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={soru.baslik ? 'onay-baslik' : undefined}
        aria-describedby="onay-metin"
        className="menu-acilis w-[min(28rem,100%)] rounded-[var(--radius-lg)] border border-border bg-bg-elevated p-5 shadow-[var(--shadow-md)]"
      >
        <div className="flex gap-3">
          <span
            className={
              tehlikeli
                ? 'grid size-9 shrink-0 place-items-center rounded-full bg-danger-soft text-danger'
                : 'grid size-9 shrink-0 place-items-center rounded-full bg-accent-soft text-accent'
            }
            aria-hidden="true"
          >
            <Simge className="size-4.5" />
          </span>
          <div className="min-w-0 space-y-1 pt-1">
            {soru.baslik && (
              <h2 id="onay-baslik" className="text-sm font-semibold text-fg">
                {soru.baslik}
              </h2>
            )}
            <p
              id="onay-metin"
              className="max-h-[50vh] overflow-y-auto whitespace-pre-line break-words text-[0.8125rem] leading-relaxed text-fg"
            >
              {soru.metin}
            </p>
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Button ref={vazgec} variant="secondary" onClick={() => soru.bitir(false)}>
            Vazgeç
          </Button>
          <Button
            ref={onay}
            onClick={() => {
              if (!kilitli()) soru.bitir(true);
            }}
            className={tehlikeli ? 'bg-none! bg-danger! text-danger-fg! hover:brightness-110' : undefined}
          >
            {soru.onayEtiketi ?? 'Onayla'}
          </Button>
        </div>
      </div>
    </div>
  );
}
